import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { STATES } from "../../../util/constant";
import {
  getSubscriptionDetail,
  setBecome,
  setBecomeOrigin,
  storeSelectedSubscriptions,
} from "../../../sagas/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// ── Styled Components ────────────────────────────────────────────────────────

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const FilterSection = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StateFilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;

  label {
    font-size: 14px;
    font-weight: 400;
    color: #000;
  }

  select {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 14px;
    box-sizing: border-box;
  }
`;

const ControlsRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  flex: ${({ $flexible }) => ($flexible ? "1 1 320px" : "0 0 auto")};

  label {
    font-size: 14px;
    font-weight: 600;
    color: #000;
    white-space: nowrap;
  }

  select,
  input {
    flex: ${({ $flexible }) => ($flexible ? "1 1 0" : "0 0 auto")};
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 14px;
    box-sizing: border-box;
    width: auto;
    min-width: ${({ $flexible }) => ($flexible ? "240px" : "140px")};
  }

  span {
    font-size: 12px;
    font-weight: 600;
    color: #000;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: stretch;

    select,
    input {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 20px;

  @media (max-width: ${breakpoints.phone}px) {
    margin-top: 12px;
  }
`;

const Table = styled.table`
  ${adminTable};

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    font-size: 11px;
  }

  tbody td {
    ${adminTableDataCell};
    font-size: 11px;
    text-align: left;
  }

  tbody td:nth-child(2) {
    text-align: left;
  }

  a {
    color: ${({ colors }) => colors.buttonBackground};
    text-decoration: none;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 11px;

    thead th {
      padding: 8px 20px 8px 8px;
    }
    tbody td {
      padding: 8px;
    }
  }
`;

const PaginationInfo = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};

  @media (max-width: ${breakpoints.phone}px) {
    text-align: center;
    font-size: 11px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid #ccc;
    background-color: #f1f1f1;
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: #e6e6e6;
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background-color: #ddd;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;

    button {
      padding: 6px 12px;
      margin-left: 0;
      margin: 4px;
      font-size: 12px;
    }
  }
`;

const Link = styled.button`
  color: ${({ colors }) => colors.labelText};
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  display: inline-block;
  width: 100%;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

const ContactName = styled.div`
  font-weight: bold;
  color: ${({ colors }) => colors.labelText};
`;

const ContactEmail = styled.div`
  color: ${({ colors }) => colors.labelText};
  font-style: italic;
  font-weight: bold;
`;

const LocationCity = styled.div`
  font-size: 11px;
`;

const LocationRegion = styled.div`
  font-size: 11px;
  font-style: italic;
`;

const PurchaseDate = styled.span`
  display: inline-block;
  font-size: 10px;
`;

const ActiveTag = styled.span`
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  color: ${({ colors }) => colors.buttonBackground};
  text-transform: uppercase;
`;

const InvoiceDetailContainer = styled.div`
  background: ${({ colors }) => colors.cardBackground};
  padding: 24px;
  border-radius: 8px;
  max-width: 1000px;
  margin: 0 auto;
  // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const InvoiceDetailTitle = styled.h2`
  margin: 0 0 16px;
  color: #DE5148;
  background-color: "#fff";
  font-size: 20px;
  font-weight: 600;
`;

const InvoiceDetailList = styled.dl`
  display: grid;
  grid-template-columns: 160px 1fr;
  row-gap: 8px;
  column-gap: 16px;
  margin: 0 0 24px;
  text-align: left;

  dt {
    font-weight: 600;
    color: #000;
    font-size: 14px;
  }

  dd {
    margin: 0;
    font-size: 14px;
    color: #000;
    min-height: 20px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    grid-template-columns: 1fr;
    dt {
      margin-top: 8px;
    }
  }
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;


// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateString(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${m}-${d}-${y}`;
}

function formatPhone(value = "") {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 10) {
    return value;
  }
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6);
  return `(${area})${prefix}-${line}`;
}

const normalizeInvoiceNumber = (value) => String(value ?? "").trim();
const normalizeCustomerId = (value) => String(value ?? "").trim();
const normalizeCustomerCode = (value) => String(value ?? "").trim();
const getActiveSelectionKey = (row) => {
  const invoiceNumber = normalizeInvoiceNumber(row?.invoice_number);
  const customerCode = normalizeCustomerCode(row?.customer_code);
  const customerId = normalizeCustomerId(row?.customer_id);
  if (customerCode && invoiceNumber) {
    return `${customerCode}::${invoiceNumber}`;
  }
  if (customerId && invoiceNumber) {
    return `${customerId}::${invoiceNumber}`;
  }
  const invoiceId = String(row?.invoice_id ?? "").trim();
  return invoiceId ? `id::${invoiceId}` : "";
};

// ── Component ───────────────────────────────────────────────────────────────

const mapInvoiceDetail = (detail) => (
  detail
    ? {
        invoiceNumber: detail.invoice_number,
        parentCode: detail.parent_code,
        startDate: detail.start_date,
        endDate: detail.end_date,
        purchaseDate: detail.purchase_date,
        poNumber: detail.po_number,
        orgName: detail.org_name,
        orgType: detail.org_type,
        orgStreet: detail.org_street,
        orgCity: detail.org_city,
        orgState: detail.org_state,
        orgZip: detail.org_zip,
        orgCountry: detail.org_country,
        contactEmail: detail.contact_email,
        contactPhone: detail.contact_phone,
        itemNumber: detail.item_number,
        productCode: detail.product_code,
        numberOfSchools: detail.number_of_schools,
        customerTag: detail.customer_tag,
        salespersonTag: detail.salesperson_tag,
        itemDescription: detail.item_description,
        comments: detail.comments,
        duration: detail.duration,
        quantity: detail.quantity,
        invoiceAmount: detail.invoice_amount,
        customerBalance: detail.customer_balance,
      }
    : null
);

export default function SubscriptionsContent({
  colors,
  allowedCustomerIds = [],
  dedupeByCustomerLatest = false,
  showOrgOnly = false,
  onOrgSelect = () => {},
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId =
    useSelector((s) => s.auth?.user?.user_id) || "anonymous";
  const activeSubscription =
    useSelector((s) => s.auth?.selected_subscriptions) || {};
  const activeInvoiceId =
    activeSubscription?.invoice_id === undefined ||
    activeSubscription?.invoice_id === null
      ? ""
      : String(activeSubscription.invoice_id);
  const activeSubscriptionKey = useMemo(
    () => getActiveSelectionKey(activeSubscription),
    [activeSubscription]
  );
  // filters & paging
  const [stateFilter, setStateFilter] = useState("0");
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSelectionKey, setActiveSelectionKey] = useState("");

  // data
  const [subscriptions, setSubscriptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contextInvoiceId, setContextInvoiceId] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const allowedSet = useMemo(() => {
    return new Set(
      (allowedCustomerIds || []).map((id) => String(id ?? "")).filter(Boolean)
    );
  }, [allowedCustomerIds]);

  const pickLatestByCustomer = (list = []) => {
    if (!dedupeByCustomerLatest) return list;
    const bestByCustomer = new Map();

    const toTime = (dateStr, fallback = 0) => {
      if (!dateStr) return fallback;
      const t = Date.parse(`${dateStr}T00:00:00`);
      return Number.isFinite(t) ? t : fallback;
    };

    list.forEach((row, index) => {
      const key = String(row?.customer_id ?? "");
      if (!key) return;
      const candidate = {
        row,
        start: toTime(row?.start_date, 0),
        end: toTime(row?.end_date, 0),
        index,
      };
      const current = bestByCustomer.get(key);
      if (!current) {
        bestByCustomer.set(key, candidate);
        return;
      }
      if (candidate.end > current.end) {
        bestByCustomer.set(key, candidate);
      } else if (candidate.end === current.end && candidate.start > current.start) {
        bestByCustomer.set(key, candidate);
      }
    });

    return Array.from(bestByCustomer.values())
      .sort((a, b) => a.index - b.index)
      .map((v) => v.row);
  };

  // fetch whenever filters/paging/search changes
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const payload = {
          state: stateFilter === "0" ? "" : stateFilter,
          page: currentPage - 1,
          items_per_page: pageSize,
          show_all:1,
        };
        if (searchTerm) payload.search = searchTerm;

        const resp = await API.getSubscriptionListing(payload);
        let rows = resp.subscriptions || [];
        if (allowedSet.size > 0) {
          rows = rows.filter((row) =>
            allowedSet.has(String(row?.customer_id ?? ""))
          );
        }
        rows = pickLatestByCustomer(rows);
        setSubscriptions(rows);
        const apiTotal = Number(resp.total) || rows.length;
        const nextTotal = apiTotal;
        setTotal(nextTotal);
      } catch (err) {
        console.error("Failed to load subscriptions:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [
    stateFilter,
    searchTerm,
    pageSize,
    currentPage,
    allowedSet,
    dedupeByCustomerLatest,
  ]);

  const pageCount = Math.ceil(total / pageSize);

  useEffect(() => {
    if (total === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (pageCount > 0 && currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount, total]);

  const hasPrev = currentPage > 1;
  const hasNext = total > 0 && currentPage < pageCount;
  const startEntry = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = total === 0 ? 0 : Math.min(currentPage * pageSize, total);

  const toTime = (val, fallback = 0) => {
    if (!val) return fallback;
    const t = new Date(`${val}T00:00:00`).getTime();
    return Number.isNaN(t) ? fallback : t;
  };

  const pickPreferredForOrg = (orgName, fallbackRow = null) => {
    const normalized = (orgName || "").trim().toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const matching = normalized
      ? subscriptions.filter(
          (r) => (r.org_name || "").trim().toLowerCase() === normalized
        )
      : [];

    const withStatus = matching.map((r) => {
      const startTime = toTime(r.start_date, 0);
      const endTime = toTime(r.end_date, 0);
      const isCurrent =
        Number.isFinite(startTime) &&
        Number.isFinite(endTime) &&
        startTime <= today.getTime() &&
        today.getTime() <= endTime;
      return { row: r, isCurrent, endTime, startTime };
    });

    const currentRows = withStatus.filter((r) => r.isCurrent);
    if (currentRows.length) {
      currentRows.sort(
        (a, b) =>
          b.endTime - a.endTime || b.startTime - a.startTime
      );
      return currentRows[0].row;
    }

    if (withStatus.length) {
      withStatus.sort(
        (a, b) =>
          b.endTime - a.endTime || b.startTime - a.startTime
      );
      return withStatus[0].row;
    }

    return fallbackRow;
  };

  const isActiveRow = (row) => {
    if (!activeSelectionKey) return false;
    return getActiveSelectionKey(row) === activeSelectionKey;
  };

  const selectSubscription = (row, { userInitiated = false } = {}) => {
    if (!row) return;
    const invoiceNumber = normalizeInvoiceNumber(row?.invoice_number);
    const customerId = normalizeCustomerId(row?.customer_id);
    const customerCode = normalizeCustomerCode(row?.customer_code);
    const matchedRow =
      invoiceNumber && customerCode
        ? subscriptions.find(
            (item) =>
              normalizeInvoiceNumber(item?.invoice_number) === invoiceNumber &&
              normalizeCustomerCode(item?.customer_code) === customerCode
          )
        : invoiceNumber && customerId
          ? subscriptions.find(
              (item) =>
                normalizeInvoiceNumber(item?.invoice_number) === invoiceNumber &&
                normalizeCustomerId(item?.customer_id) === customerId
            )
          : null;
    const preferred =
      matchedRow || pickPreferredForOrg(row.org_name, row) || row;
    const effectiveRow = preferred;
    const effectiveKey = getActiveSelectionKey(effectiveRow);
    const isSameSelection =
      Boolean(effectiveKey) && effectiveKey === activeSubscriptionKey;

    if (isSameSelection && !userInitiated) {
      return;
    }

    if (effectiveKey && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          `admincentral.subscription.selected.${userId}`,
          JSON.stringify(effectiveKey)
        );
      } catch (_err) {
        // ignore storage errors
      }
    }

    dispatch(storeSelectedSubscriptions(effectiveRow));
    dispatch(
      getSubscriptionDetail(
        effectiveRow.invoice_id,
        effectiveRow.customer_id,
        effectiveRow.customer_code
      )
    );

    if (userInitiated) {
      dispatch(setBecomeOrigin("subscription"));
      dispatch(setBecome(1));
      onOrgSelect();
      navigate("/admincentral/dashboard");
    }
  };

  useEffect(() => {
    if (!subscriptions.length) return;
    if (activeInvoiceId || activeSubscriptionKey) return;
    const fallback = subscriptions[subscriptions.length - 1];
    const preferred = pickPreferredForOrg(fallback?.org_name, fallback);
    selectSubscription(preferred || fallback, { userInitiated: false });
  }, [subscriptions, activeInvoiceId, activeSubscriptionKey]);

  const columns = showOrgOnly
    ? [{ key: "org", label: "Org" }]
    : [
        // { key: "invoice", label: "Invoice" },
        { key: "org", label: "Org" },
        // { key: "contact_name", label: "Contact" },
        // { key: "location", label: "Location" },
        // { key: "phone", label: "Phone" },
        // { key: "startDate", label: "Start Date" },
        // { key: "endDate", label: "End Date" },
        // { key: "tag", label: "Tag" },
      ];

  const handleInvoiceClick = async (row) => {
    setDetailLoading(true);
    try {
      const resp = await API.getSubscriptionDetail({
        invoice_id: row.invoice_id,
        customer_id: row.customer_id,
        customer_code: row.customer_code,
      });
      if (resp?.invoice_detail?.length) {
        setInvoiceDetail(mapInvoiceDetail(resp.invoice_detail[0]));
        setContextInvoiceId(row.invoice_id);
      } else {
        setInvoiceDetail(null);
        setContextInvoiceId(null);
      }
    } catch (err) {
      console.error("Failed to fetch invoice detail", err);
      setInvoiceDetail(null);
      setContextInvoiceId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailCancel = () => {
    setContextInvoiceId(null);
    setInvoiceDetail(null);
  };

  const onOrgClick = (row) => {
    setActiveSelectionKey(getActiveSelectionKey(row));
    selectSubscription(row, { userInitiated: true });
  };


  if (contextInvoiceId && invoiceDetail) {
    return (
      <InvoiceDetailContainer colors={colors}>
        <InvoiceDetailTitle colors={colors}>
          Viewing Invoice {invoiceDetail.invoiceNumber || contextInvoiceId}
        </InvoiceDetailTitle>
        <InvoiceDetailList colors={colors}>
          {[
            { label: "Parent Code", value: invoiceDetail.parentCode },
            { label: "Start Date", value: formatDateString(invoiceDetail.startDate) },
            { label: "End Date", value: formatDateString(invoiceDetail.endDate) },
            { label: "Purchase Date", value: formatDateString(invoiceDetail.purchaseDate) },
            { label: "PO Number", value: invoiceDetail.poNumber },
            { label: "Organization", value: invoiceDetail.orgName },
            { label: "Org Type", value: invoiceDetail.orgType },
            { label: "Org Street", value: invoiceDetail.orgStreet },
            { label: "Org City", value: invoiceDetail.orgCity },
            { label: "Org State", value: invoiceDetail.orgState },
            { label: "Org Zip", value: invoiceDetail.orgZip },
            { label: "Org Country", value: invoiceDetail.orgCountry },
            { label: "Contact E-mail", value: invoiceDetail.contactEmail },
            { label: "Contact Phone", value: formatPhone(invoiceDetail.contactPhone || "") },
            { label: "Item Number", value: invoiceDetail.itemNumber },
            { label: "Product Code", value: invoiceDetail.productCode },
            { label: "Num of Schools", value: invoiceDetail.numberOfSchools },
            { label: "Customer Tag", value: invoiceDetail.customerTag },
            { label: "Salesperson Tag", value: invoiceDetail.salespersonTag },
            { label: "Item Description", value: invoiceDetail.itemDescription },
            { label: "Comments", value: invoiceDetail.comments },
            { label: "Duration", value: invoiceDetail.duration },
            { label: "Quantity", value: invoiceDetail.quantity },
            { label: "Invoice Amount", value: invoiceDetail.invoiceAmount ? `$${invoiceDetail.invoiceAmount}` : invoiceDetail.invoiceAmount },
            { label: "Customer Balance", value: invoiceDetail.customerBalance ? `$${invoiceDetail.customerBalance}` : invoiceDetail.customerBalance },
          ].map(({ label, value }) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value || "\u00a0"}</dd>
            </React.Fragment>
          ))}
        </InvoiceDetailList>
        <CancelButton colors={colors} onClick={handleDetailCancel}>
          Cancel
        </CancelButton>
      </InvoiceDetailContainer>
    );
  }

  return (
    <div>
      <FilterSection>
        <StateFilterRow colors={colors}>
          <label>State Filter:</label>
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="0">None</option>
            {Object.entries(STATES).map(([name, abbr]) => (
              <option key={abbr} value={abbr}>{name}</option>
            ))}
          </select>
        </StateFilterRow>

        <ControlsRow>
          <ControlGroup colors={colors}>
            <label>Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>entries</span>
          </ControlGroup>

          <ControlGroup colors={colors} $flexible>
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search invoice, org…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </ControlGroup>
        </ControlsRow>
      </FilterSection>

      {loading ? (
        <p>Loading…</p>
      ) : detailLoading ? (
        <p>Loading invoice detail…</p>
      ) : (
        <>
          <TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  {columns.map(({ key, label }) => (
                    <th key={key}>{label}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {subscriptions.map((row) =>
                  showOrgOnly ? (
                    <tr key={`${row.customer_id}-${row.invoice_id}`}>
                      <td>
                        <Link
                          colors={colors}
                          type="button"
                          style={{ fontWeight: 600 }}
                          onClick={() => onOrgClick(row)}
                        >
                          {row.org_name}
                          {isActiveRow(row) && (
                            <ActiveTag colors={colors}>(Active)</ActiveTag>
                          )}
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    <tr key={row.invoice_id}>
                      {/* <td>
                        <Link
                          colors={colors}
                          type="button"
                          style={{fontWeight: 600}}
                          onClick={() => handleInvoiceClick(row)}
                        >
                          {row.invoice_number}
                        </Link>
                        <br />
                        <PurchaseDate>
                          PD: {formatDateString(row.purchase_date)}
                        </PurchaseDate>
                      </td> */}
                      <td>
                        <Link
                          colors={colors}
                          type="button"
                          style={{fontWeight: 600}}
                          onClick={() => onOrgClick(row)}
                        >
                          {row.org_name}
                          {isActiveRow(row) && (
                            <ActiveTag colors={colors}>(Active)</ActiveTag>
                          )}
                        </Link>
                      </td>
                      {/* <td className="userCell">
                        <ContactName colors={colors}>{row.contact_name}</ContactName>
                        <ContactEmail colors={colors}>
                          {row.contact_email}
                        </ContactEmail>
                      </td>
                      <td>
                        <LocationCity>{row.org_city}</LocationCity>
                        <LocationRegion>
                          {row.org_state} {row.org_zip}
                        </LocationRegion>
                      </td>
                      <td>{formatPhone(row.contact_phone || "")}</td>
                      <td>{formatDateString(row.start_date)}</td>
                      <td>{formatDateString(row.end_date)}</td>
                      <td>{row.salesperson_tag}</td> */}
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          </TableWrapper>

          <PaginationInfo colors={colors}>
            Showing {startEntry} to {endEntry} of {total} entries
          </PaginationInfo>
          <Pagination>
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={!hasPrev}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((p) => (hasNext ? Math.min(p + 1, pageCount) : p))
              }
              disabled={!hasNext}
            >
              Next
            </button>
          </Pagination>
        </>
      )}
    </div>
  );
}
